import classNames from "classnames";
import AppContext from "PFApp/app_context";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import { Icon } from "PFComponents/icon";
import Pill from "PFComponents/pill/pill";
import AutoSelect from "PFComponents/select/autoselect";
import InputFieldSet from "PFComponents/text/input_field_set";
import Tooltip from "PFComponents/tooltip/tooltip";
import { useCustomValuesInvalidate } from "PFCore/hooks/queries/custom_fields/admin";
import { useApproveCustomValues } from "PFCore/hooks/queries/custom_fields/admin/custom_values_bulk/use_approve_custom_values";
import i18n from "PFCore/i18n";
import { mergeCustomValuesByType } from "PFCore/services/admin/custom_values";
import { fetchAutocompleteCustomValues } from "PFCore/services/autocomplete/fetch_autocomplete_custom_values";
import { ApiRoute } from "PFCore/utilities/routes";
import { GROWL_ACTIONS } from "PFReducers/growl_reducer";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import css from "./custom_value.module.scss";

const questionMarkStatus = (requireCuration, requiresApproval, adminApproved, curatedAt) => {
  if (requireCuration || requiresApproval) {
    if (!curatedAt && !adminApproved) {
      return (
        <Tooltip content={i18n.t("admin:customFields.dictionaries.unchecked.isUnchecked")}>
          <div className={css.tooltip}>
            <Icon name="question" />
          </div>
        </Tooltip>
      );
    } else {
      return null;
    }
  }
};

const CustomValue = ({
  item,
  cfId,
  editCF,
  setEditCF,
  index,
  mergeList,
  setMergeList,
  selectedToMerge,
  setSelectedToMerge,
  requiresCuration,
  requiresApproval,
  isRankable,
  isHierarchical,
  isDragged
}) => {
  const { t } = useTranslation("admin");
  const { dispatch } = useContext(AppContext);
  const [error, setError] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const { invalidate: invalidateCustomValues } = useCustomValuesInvalidate();
  const { mutateAsync: approveCustomValues } = useApproveCustomValues();

  const {
    id,
    value,
    name,
    blocked,
    admin_approved: adminApproved,
    global_id: globalId,
    curated_at: curatedAt,
    connected_profiles_ids: connectedProfilesIds
  } = item;

  const [inputKey, setInputKey] = useState(value);

  const handleSaveAndSeed = () => {
    $.ajax({
      url: ApiRoute(`/api/admin/custom_fields/${cfId}/custom_values/${id}`),
      data: { ...item, value: editCF[id] || value },
      method: "PUT"
    })
      .then(() => {
        $.ajax({
          url: ApiRoute(`/api/admin/custom_fields/${cfId}/custom_values/${id}/seed`),
          data: { skill_name: editCF[id] || value, seed_now: true },
          method: "POST",
          api_version: 1
        }).then(() => invalidateCustomValues());
      })
      .catch((err) => {
        const errorKey = Object.keys(err?.responseJSON?.errors)?.[0];
        setError(err?.responseJSON?.errors?.[errorKey]?.[0]);
      });
  };

  const handleEditSwitch = () => {
    setIsEditMode((prev) => !prev);
    setEditCF((prev) => ({ ...prev, [id]: null }));
    setInputKey(isEditMode ? value : `${value}_edit`);
  };

  const handleSave = () => {
    $.ajax({
      url: ApiRoute(`/api/admin/custom_fields/${cfId}/custom_values/${id}`),
      data: { ...item, ...editCF[id] },
      method: "PUT"
    }).then(() => {
      invalidateCustomValues();
      setEditCF((prev) => ({ ...prev, [id]: null }));
      setIsEditMode(false);
    });
  };

  const handleSeed = () => {
    $.ajax({
      url: ApiRoute(`/api/admin/custom_fields/${cfId}/custom_values/${id}/seed`),
      data: { skill_name: editCF[id] || value, seed_now: true },
      method: "POST",
      api_version: 1
    });
  };

  const handleRemove = () => {
    if (
      confirm(
        connectedProfilesIds.length > 0
          ? t("customFields.dictionaries.removeConfirm.connected", {
              profiles_ids: connectedProfilesIds
            })
          : t("customFields.dictionaries.removeConfirm.notConnected")
      )
    ) {
      $.ajax({
        url: ApiRoute(`/api/admin/custom_fields/${cfId}/custom_values/${id}`),
        method: "DELETE",
        api_version: 1
      })
        .then(() => invalidateCustomValues())
        .catch(({ responseJSON }) => {
          const message = responseJSON.errors?.Task
            ? t("customFields.dictionaries.errors.remove.tasks", {
                tasks_count: responseJSON.errors.Task.length
              })
            : t("customFields.dictionaries.errors.remove.generic");

          dispatch({
            type: GROWL_ACTIONS.GROWL_RENDER,
            payload: {
              message: `${message} ${t("customFields.dictionaries.errors.remove.common")}`,
              kind: "error"
            }
          });
        });
    }
  };

  const handleApprove = () => {
    approveCustomValues({ ids: [id] });
  };

  const handleMerge = () => {
    setMergeList((prev) => {
      const newList = [...prev];
      newList[index] = { renderMerge: !newList[index].renderMerge };
      return newList;
    });
  };

  const handleMergeButtonClick = () => {
    if (
      window.confirm(t("customFields.dictionaries.mergeConfirm", { from: value, to: selectedToMerge?.text }))
    ) {
      mergeCustomValuesByType(cfId, selectedToMerge?.id, [id])
        .then(() => invalidateCustomValues())
        .catch(() =>
          dispatch({
            type: GROWL_ACTIONS.GROWL_RENDER,
            payload: {
              message: t("customFields.dictionaries.errors.merge"),
              kind: "error"
            }
          })
        );
    }
  };

  const handleCloseMergeButtonClick = () => {
    setMergeList((prev) => {
      const newList = [...prev];
      newList[index] = { renderMerge: false };
      return newList;
    });
  };

  const handleEditCustomValue = (attribute) => {
    const customValue = { ...editCF[id], ...attribute };
    setEditCF({ ...editCF, [id]: customValue });
  };

  return (
    <div key={id} className={classNames(css.inputRendererWrapper, { [css.dragged]: isDragged })}>
      <div className={css.inputRenderer}>
        <span className={css.orderable}>{isHierarchical ? "::" : ""}</span>
        <InputFieldSet
          key={inputKey}
          defaultValue={value}
          inputType="text"
          qaId={`custom-field-dictionary`}
          handleChange={(value) => handleEditCustomValue({ value })}
          icon={questionMarkStatus(requiresCuration, requiresApproval, adminApproved, curatedAt)}
          rootClassName={classNames(css.inputValue, { [css.draggable]: !isEditMode })}
          locked={blocked && requiresCuration}
          lockedTip={t("customFields.dictionaries.disabled")}
          innerStyle={{ paddingRight: 30 }}
          error={error}
        />
        <div className={css.lineButtons}>
          {requiresCuration && curatedAt ? (
            <Pill style={{ marginRight: 10 }}>{globalId ? "Global" : "Local"}</Pill>
          ) : (
            requiresApproval && (
              <Pill style={{ marginRight: 10 }}>{adminApproved ? "Approved" : "Unapproved"}</Pill>
            )
          )}
          <Button small kind="secondary" onClick={handleEditSwitch}>
            {isEditMode ? t("translation:cancel") : t("translation:edit")}
          </Button>
          {editCF[id] && (
            <Button small onClick={handleSave}>
              {t("form.buttons.save")}
            </Button>
          )}
          {isRankable && !blocked && (
            <Button small kind="secondary" onClick={handleSaveAndSeed}>
              {t("form.buttons.saveAndSeed")}
            </Button>
          )}
          {isRankable && blocked && requiresCuration && (
            <Button small kind="secondary" onClick={handleSeed}>
              {t("form.buttons.seed")}
            </Button>
          )}
          <Button kind="danger" small onClick={handleRemove}>
            {t("form.buttons.remove")}
          </Button>
          {requiresApproval && !adminApproved && !blocked && (
            <Button small onClick={handleApprove}>
              {t("form.buttons.approve")}
            </Button>
          )}
          {!blocked && (
            <Button kind="tertiary" small onClick={handleMerge}>
              {t("form.buttons.merge")}
            </Button>
          )}
        </div>
      </div>

      {mergeList?.[index]?.renderMerge && (
        <div className={css.inputMerger}>
          <div className={css.mergeHeader}>
            <div>{t("form.buttons.merge")}</div>
            <ActionIcon name="cross" size="xs" onClick={handleCloseMergeButtonClick} />
          </div>
          <AutoSelect
            closeOnChange
            multi={false}
            query={(term) => fetchAutocompleteCustomValues({ term, type: name, global: !!globalId })}
            filterOptions={(response) => response.filter((item) => item.id !== id)}
            handleChange={(value) => setSelectedToMerge(value?.[0])}
          />
          <div className={css.mergeFooter}>
            <Button
              text={t("form.buttons.merge")}
              disabled={!selectedToMerge}
              onClick={handleMergeButtonClick}
            />
          </div>
        </div>
      )}
    </div>
  );
};

CustomValue.propTypes = {
  item: PropTypes.shape({
    id: PropTypes.number,
    value: PropTypes.string,
    blocked: PropTypes.bool,
    admin_approved: PropTypes.bool,
    connected_profiles_ids: PropTypes.array,
    name: PropTypes.string,
    curated_at: PropTypes.string,
    global_id: PropTypes.string,
    ordinal: PropTypes.number
  }).isRequired,
  cfId: PropTypes.number,
  editCF: PropTypes.object.isRequired,
  setEditCF: PropTypes.func,
  index: PropTypes.number,
  mergeList: PropTypes.array,
  setMergeList: PropTypes.func.isRequired,
  selectedToMerge: PropTypes.object,
  setSelectedToMerge: PropTypes.func,
  requiresCuration: PropTypes.bool,
  requiresApproval: PropTypes.bool,
  isRankable: PropTypes.bool,
  isHierarchical: PropTypes.bool.isRequired,
  isDragged: PropTypes.bool.isRequired
};
export default CustomValue;
